import { addDays, differenceInCalendarDays, format, parseISO, startOfDay } from 'date-fns';
import type { TechCertification } from '@/lib/types';

/** Default job length when no end date is picked (matches the server's 14-day hitch). */
const DEFAULT_JOB_DAYS = 14;
/** Certs expiring this many days after the job ends still get an advisory warning. */
const EXPIRY_AFTER_JOB_WARNING_DAYS = 30;

export interface CertIssue {
  cert: TechCertification;
  /** True when the tech is uncertified for part of the job (needs a Manager override). */
  blocking: boolean;
  message: string;
}

/**
 * Check each cert against the job's dates, not today: expired, lapsing before
 * the job starts, lapsing during the job, or lapsing within 30 days after it.
 */
export function classifyCertsForJob(
  certs: TechCertification[],
  startDate: string,
  endDate: string,
  today: Date = new Date(),
): CertIssue[] {
  if (!startDate) return [];
  const start = parseISO(startDate);
  const end = endDate ? parseISO(endDate) : addDays(start, DEFAULT_JOB_DAYS - 1);
  const todayDay = startOfDay(today);
  const issues: CertIssue[] = [];
  for (const cert of certs) {
    if (cert.status === 'revoked') continue;
    const expiry = cert.expiry_date ? parseISO(cert.expiry_date) : null;
    const label = expiry ? format(expiry, 'd MMM') : '';
    if (cert.status === 'expired' || (expiry && expiry < todayDay)) {
      issues.push({ cert, blocking: true, message: `${cert.cert_name} is expired` });
    } else if (!expiry) {
      continue;
    } else if (expiry < start) {
      issues.push({ cert, blocking: true, message: `${cert.cert_name} expires ${label}, before this job starts` });
    } else if (expiry <= end) {
      issues.push({ cert, blocking: true, message: `${cert.cert_name} expires ${label}, during this job` });
    } else if (expiry <= addDays(end, EXPIRY_AFTER_JOB_WARNING_DAYS)) {
      const daysAfter = differenceInCalendarDays(expiry, end);
      issues.push({
        cert,
        blocking: false,
        message: `${cert.cert_name} expires ${label}, ${daysAfter} ${daysAfter === 1 ? 'day' : 'days'} after this job`,
      });
    }
  }
  return issues;
}
